import { createSelector } from "@reduxjs/toolkit";

// ─── Simple field selectors ───────────────────────────────────────────────
// Central source of truth for all Redux state access.
// If the state shape changes, update here instead of 46+ component files.

// Stable fallback for "missing optional object" selectors. Returning a fresh
// `{}` from a selector mints a new reference every call, so useSelector can
// never bail out — the subscriber re-renders on EVERY dispatch to any slice.
// Share one frozen reference instead.
const EMPTY_OBJECT = Object.freeze({});

// Core diagram (workflow slice) — projected from the normalized view-model.
// Memoized so subscribers re-render only when the underlying keyed maps / order
// arrays change (same frequency as the old arrays), not on unrelated writes.
const selectVmNodesById = (state) => state.workflow.vm.nodesById;
const selectVmNodeOrder = (state) => state.workflow.vm.nodeOrder;
const selectVmEdgesById = (state) => state.workflow.vm.edgesById;
const selectVmEdgeOrder = (state) => state.workflow.vm.edgeOrder;

export const selectVm = (state) => state.workflow.vm;

export const selectNodes = createSelector(
  [selectVmNodesById, selectVmNodeOrder],
  (byId, order) => order.map((id) => byId[id])
);
export const selectEdges = createSelector(
  [selectVmEdgesById, selectVmEdgeOrder],
  (byId, order) => order.map((id) => byId[id])
);
export const selectActivities = (state) => state.workflow.vm.activities;

// Derives activities with type info. When the workflow diagram editor has been
// opened, vm.activities is populated and used directly (has uid/name/type).
// On direct navigation to a view editor (hard refresh), vm.activities is empty;
// fall back to activityDataFields which are loaded by useWorkflowFields and
// now carry activity_type from the backend.
export const selectActivitiesOrDerived = createSelector(
    (state) => state.workflow.vm.activities,
    (state) => state.workflow.activityDataFields,
    (vmActivities, activityDataFields) => {
        if (vmActivities && vmActivities.length > 0) return vmActivities;
        return (activityDataFields || []).map((af) => ({
            uid: af.activity_uid,
            name: af.activity_name || af.activity_uid,
            type: af.activity_type || '',
        }));
    }
);

// Activities for the VIEW EDITORS (RV/DV/AV/Chart), derived STRICTLY from
// activityDataFields — the per-view source loaded by useWorkflowFields and
// keyed to the URL's view + workflow version. Unlike selectActivitiesOrDerived,
// this NEVER reads vm.activities, which is owned by the diagram editor
// (Editor.js), keyed to whatever version was last opened there, and is not
// cleared on view navigation. Reading vm.activities in a view editor lets the
// activity LIST (from vm.activities) disagree with the per-activity FIELDS
// (from activityDataFields) whenever the last-opened diagram differs from the
// view in the URL — the list shows the wrong workflow's activities and every
// folder renders empty because the uids don't match. Deriving both from one
// source keeps the sidebar always consistent with the URL. activityDataFields
// carries activity_uid / activity_name / activity_type from the backend, so
// nothing (incl. INIT-palette detection) is lost.
export const selectActivitiesFromDataFields = createSelector(
    (state) => state.workflow.activityDataFields,
    (activityDataFields) =>
        (activityDataFields || []).map((af) => ({
            uid: af.activity_uid,
            name: af.activity_name || af.activity_uid,
            type: af.activity_type || '',
        }))
);

export const selectCanUndo = (state) => state.workflow.history.past.length > 0;
export const selectCanRedo = (state) => state.workflow.history.future.length > 0;
export const selectUnsavedActivityUIDs = (state) => state.workflow.unsavedActivityUIDs || [];
export const selectWorkflow = (state) => state.workflow.workflow;

// True when the loaded workflow version is published (Cut Version frozen).
// Editor components use this to switch to read-only mode — disabling drag,
// connect, drop, delete in WorkflowCanvas and hiding Save in Toolbar — so
// users hit the constraint at the UI layer instead of via a 423 on save.
// Server-side freeze gate still enforces; this is purely UX.
export const selectIsVersionPublished = (state) => !!state.workflow.workflow?.is_published;

// Selection (workflow slice — kept here to avoid cross-slice sync).
// The slice stores only an ID; the node/edge is derived from the vm here. That
// removes the denormalized copy the reducers used to re-sync on setGraph,
// setNodes, every drag frame, undo, redo and four refreshSelectedNode call
// sites — a second source of truth every mutation path had to remember. Being
// derived, it is also always live: editing the selected node's data no longer
// depends on a reducer remembering to refresh a snapshot.
//
// Memoized, so the reference changes only when the selected node itself does.
// The old copy was reassigned on every drag frame, re-rendering all six
// consumers whenever the dragged node happened to be the selected one.
export const selectSelectedNodeId = (state) => state.workflow.selectedNodeId;
export const selectSelectedEdgeId = (state) => state.workflow.selectedEdgeId;
export const selectSelectedNode = createSelector(
  [selectVmNodesById, selectSelectedNodeId],
  (byId, id) => (id ? byId[id] || null : null)
);
export const selectSelectedEdge = createSelector(
  [selectVmEdgesById, selectSelectedEdgeId],
  (byId, id) => (id ? byId[id] || null : null)
);

// Data fields (workflow slice)
export const selectWorkflowDataFields = (state) => state.workflow.workflowDataFields;
export const selectActivityDataFields = (state) => state.workflow.activityDataFields;

// Permissions & triggers (workflow slice)
export const selectActivityPermissions = (state) => state.workflow.activityPermissions;
export const selectActivityTriggers = (state) => state.workflow.activityTriggers;
export const selectActivityPrefills = (state) => state.workflow.activityPrefills;
export const selectActivityPrefillFlow = (state) => state.workflow.activityPrefillFlow;

// Org & pipeline (workflow slice)
export const selectCurrentOrg = (state) => state.workflow.currentOrg;

// Workflow-state options derived from diagram nodes (id + label), for field
// editors that target a state (e.g. button_group). Memoized so subscribers
// re-render only when the state set actually changes — NOT on every unrelated
// node mutation (drag, resize, label edit on other node types).
export const selectStateNodes = createSelector([selectNodes], (nodes) =>
  (nodes || [])
    .filter((n) => n.type === "stateNode")
    .map((n) => ({
      id: n.data?.uid || n.id,
      name: n.data?.label || n.data?.name || "State",
    }))
);

// ─── UI state (ui slice) ─────────────────────────────────────────────────

export const selectTheme = (state) => state.ui.theme;
export const selectActiveView = (state) => state.ui.activeView;
// id → display label for every node. Deliberately NOT the whole node: a node
// component that subscribes to `nodes` re-renders on every position change, so
// dragging one node re-renders all of them. Paired with react-redux's
// shallowEqual at the call site this stays inert during a drag, because labels
// don't move.
export const selectNodeLabelById = createSelector([selectNodes], (nodes) => {
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n.data?.label || n.data?.title || "?"; });
  return byId;
});

// id → node type, split from the label map on purpose: both must stay
// STRING-valued so shallowEqual at the call site sees equal values and skips
// the re-render. Returning {label, type} objects would allocate fresh values
// every time and defeat that.
export const selectNodeTypeById = createSelector([selectNodes], (nodes) => {
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n.type; });
  return byId;
});

export const selectHoveredActivity = (state) => state.ui.hoveredActivity;
export const selectHoveredEdgeId = (state) => state.ui.hoveredEdgeId;
export const selectSearchHighlight = (state) => state.ui.searchHighlight;
// Returns true iff ANY editor (legacy direct dispatcher or useDirtyState
// instance) has registered itself as dirty. External contract unchanged.
export const selectIsDirty = (state) => state.ui.dirtyOwners.length > 0;
// True iff the LEGACY direct-dispatcher owner is registered. Deliberately NOT
// selectIsDirty: that one is true when ANY owner is dirty, so a caller using it
// to skip a redundant `setIsDirty(true)` would also skip while a SIBLING panel
// happened to be dirty — and its own dirty flag would then disappear the moment
// that sibling cleaned up. Ask about your own owner, not about the aggregate.
export const selectIsLegacyDirty = (state) => state.ui.dirtyOwners.includes('legacy');
export const selectToast = (state) => state.ui.toast;
export const selectBreadcrumbs = (state) => state.ui.breadcrumbs;
// Target-mode selectors — see uiSlice.js.
export const selectTargetMode = (state) => state.ui.targetMode;
export const selectIsTargetModeReady = (state) => !!state.ui.targetModeResolved;
// Gate for authoring controls: true only once the mode has resolved and the
// target is not a consumer lock. Prefer this over composing the two selectors
// above so a call site can't forget the unresolved case.
export const selectImageTierVisible = (state) =>
  !!state.ui.targetModeResolved && state.ui.targetMode !== "consumer";
export const selectSelectedActivityUid = (state) => state.ui.selectedActivityUid;

// Modals (ui slice)
export const selectConfirmation = (state) => state.ui.confirmation || EMPTY_OBJECT;
export const selectAddActivityModal = (state) => state.ui.addActivityModal || EMPTY_OBJECT;
export const selectAddExistingActivityModal = (state) => state.ui.addExistingActivityModal || EMPTY_OBJECT;
export const selectJsonModal = (state) => state.ui.jsonModal || EMPTY_OBJECT;
export const selectTriggerFlowPopup = (state) => state.ui.triggerFlowPopup;

// ─── Views & Screens (views slice) ───────────────────────────────────────

export const selectRecordViews = (state) => state.views.recordViews;
export const selectSelectedRecordView = (state) => state.views.selectedRecordView;
export const selectDetailViews = (state) => state.views.detailViews;
export const selectSelectedDetailView = (state) => state.views.selectedDetailView;

export const selectActivityViews = (state) => state.views.activityViews;
export const selectSelectedActivityView = (state) => state.views.selectedActivityView;

export const selectChartTemplates = (state) => state.views.chartTemplates;
export const selectSelectedChartTemplate = (state) => state.views.selectedChartTemplate;
export const selectChartScreens = (state) => state.views.chartScreens;
export const selectSelectedChartScreen = (state) => state.views.selectedChartScreen;
export const selectMcScreens = (state) => state.views.mcScreens;
export const selectSelectedMcScreen = (state) => state.views.selectedMcScreen;

export const selectScreens = (state) => state.views.screens;
export const selectSelectedScreen = (state) => state.views.selectedScreen;
export const selectDvScreens = (state) => state.views.dvScreens;
export const selectSelectedDvScreen = (state) => state.views.selectedDvScreen;
export const selectRvScreens = (state) => state.views.rvScreens;
export const selectSelectedRvScreen = (state) => state.views.selectedRvScreen;
export const selectAvScreens = (state) => state.views.avScreens;
export const selectSelectedAvScreen = (state) => state.views.selectedAvScreen;

// Header & Form screens (views slice)
export const selectHeaderConfigs = (state) => state.views.headerConfigs;
export const selectSelectedHeaderConfig = (state) => state.views.selectedHeaderConfig;
export const selectSelectedFormDevice = (state) => state.views.selectedFormDevice;

// React Components (views slice)
export const selectReactComponents = (state) => state.views.reactComponents;
export const selectSelectedReactComponent = (state) => state.views.selectedReactComponent;

// ─── AI builder (builder slice) ──────────────────────────────────────────
// Plain field selectors: each returns the stored reference, so useSelector
// bails out unless that exact field was replaced (S3 — was 28 inline paths).

export const selectBuilderSession = (state) => state.builder.session;
export const selectBuilderMessages = (state) => state.builder.messages;
export const selectBuilderIsLoading = (state) => state.builder.isLoading;
export const selectBuilderActiveTab = (state) => state.builder.activeTab;
export const selectBuilderSelectedNode = (state) => state.builder.selectedNode;
export const selectBuilderNodes = (state) => state.builder.nodes;
export const selectBuilderEdges = (state) => state.builder.edges;
export const selectBuilderStates = (state) => state.builder.states;
export const selectBuilderActivities = (state) => state.builder.activities;
export const selectBuilderTransitions = (state) => state.builder.transitions;
export const selectBuilderStageGates = (state) => state.builder.stageGates;
export const selectBuilderWorkflowDataFields = (state) => state.builder.workflowDataFields;
export const selectBuilderActivityDataFields = (state) => state.builder.activityDataFields;
export const selectBuilderRecordViews = (state) => state.builder.recordViews;
export const selectBuilderActivityTriggers = (state) => state.builder.activityTriggers;
export const selectBuilderActivityPermissions = (state) => state.builder.activityPermissions;
export const selectBuilderWorkflowName = (state) => state.builder.workflowName;
